import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  Menu,
  MenuButton,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { Add16Regular, Search16Regular, WindowConsole20Regular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import type { Device } from '../api/types';
import { useMe } from '../auth/MeProvider';
import { NoAccess } from '../components/NoAccess';
import { PageHeader } from '../components/PageHeader';
import { networksFromDevices } from '../lib/networks';
import { useShell } from '../lib/shell';
import { onlineIds } from '../lib/status';
import { vt } from '../theme';
import { PinMark } from './devices/AddressFlyout';
import { DeviceTable } from './devices/DeviceTable';
import { POOL_HOSTS, allTags, filterDevices, poolUsage } from './devices/model';

/** The overlay's DNS suffix (CONTRACT 3.3 "Device names"): every device resolves as `<name>.vpn`. */
const DNS_SUFFIX = 'vpn';
const ALL = '*';

const useStyles = makeStyles({
  toolbar: { display: 'flex', alignItems: 'center', columnGap: '10px' },
  // The mockup's filter row is 32px tall (Fluent medium) with small-size text.
  search: { width: '260px', height: '32px', borderRadius: tokens.borderRadiusCircular },
  filter: { height: '32px', paddingInline: '12px', fontSize: '12.5px', fontWeight: tokens.fontWeightRegular, color: vt.fg2 },
  filterOn: { backgroundColor: vt.brandTint, color: vt.fg1 },
  count: { marginLeft: 'auto', fontSize: '11.5px', color: vt.fg3 },
  empty: { color: vt.fg3, paddingBlock: '12px', margin: 0 },
  sectionHead: { display: 'flex', alignItems: 'baseline', columnGap: '16px', marginBottom: '10px' },
  sectionTitle: { margin: 0, fontSize: '17px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  hint: { fontSize: '11.5px', color: vt.fg3 },
  pools: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '16px' },
  inset: { borderRadius: '8px', backgroundColor: vt.surface3, boxSizing: 'border-box' },
  pool: { padding: '14px 16px', display: 'flex', flexDirection: 'column', rowGap: '14px' },
  poolHead: { display: 'flex', alignItems: 'baseline', columnGap: '10px' },
  poolName: { fontSize: '12.5px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11.5px', color: vt.fg3 },
  monoStrong: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg1 },
  track: { height: '6px', borderRadius: '3px', backgroundColor: vt.surface2, overflow: 'hidden' },
  bar: { height: '100%', borderRadius: '3px', backgroundColor: vt.green },
  poolFoot: { display: 'flex', justifyContent: 'space-between' },
  faint: { color: vt.fg4 },
  naming: { display: 'grid', gridTemplateColumns: '42fr 58fr', columnGap: '20px', alignItems: 'start' },
  kvs: { margin: 0, padding: '16px 18px 4px', display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: '10px' },
  k: { color: vt.fg2 },
  v: { margin: 0, textAlign: 'end' },
  split: { padding: '12px 18px 14px', borderTop: `1px solid ${vt.surface2}`, marginTop: '12px' },
  splitRow: { display: 'inline-flex', alignItems: 'center', columnGap: '12px', fontSize: '12px', color: vt.fg1 },
  // Drawn rather than a disabled Switch: split DNS is always on, and a disabled switch reads as off.
  toggle: {
    position: 'relative',
    width: '36px',
    height: '20px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: vt.brand,
    flexShrink: 0,
  },
  knob: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralForegroundOnBrand,
  },
  splitNote: { display: 'block', fontSize: '10.5px', color: vt.fg3 },
  records: { padding: '12px 18px 14px' },
  recordGrid: { display: 'grid', gridTemplateColumns: '1fr 160px', rowGap: '6px', margin: 0 },
  colLabel: {
    fontSize: '11.5px',
    color: vt.fg3,
    paddingBottom: '8px',
    marginBottom: '2px',
    borderBottom: `1px solid ${vt.surface2}`,
  },
  record: { display: 'inline-flex', alignItems: 'center', columnGap: '8px', margin: 0 },
  shortcut: { display: 'flex', alignItems: 'center', columnGap: '8px', marginTop: '10px', color: vt.fg4 },
});

/** Shared with the old table: how a device gets onto the list in the first place. */
function EnrolText() {
  return (
    <>
      On a Windows PC: unzip the client, run <code>.\vipper install</code>, then <code>vipper login</code> - the PC
      enrolls itself under its computer name.
    </>
  );
}

function AddDevice() {
  return (
    <Dialog>
      <DialogTrigger disableButtonEnhancement>
        <Button appearance="primary" shape="circular" size="small" icon={<Add16Regular />}>
          Add device
        </Button>
      </DialogTrigger>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add a device</DialogTitle>
          <DialogContent>
            Devices enrol themselves through the VIPPER VPN client; there is nothing to create here. <EnrolText />
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary">Got it</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function FilterMenu({
  label,
  allLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  allLabel: string;
  value: string | null;
  options: string[];
  onChange: (v: string | null) => void;
}) {
  const s = useStyles();
  return (
    <Menu
      checkedValues={{ [label]: [value ?? ALL] }}
      onCheckedValueChange={(_e, data) => {
        const v = data.checkedItems[0] ?? ALL;
        onChange(v === ALL ? null : v);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <MenuButton
          appearance="outline"
          shape="circular"
          size="small"
          className={mergeClasses(s.filter, value !== null && s.filterOn)}
          aria-label={`${label}: ${value ?? allLabel}`}
        >
          {value ?? allLabel}
        </MenuButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItemRadio name={label} value={ALL}>
            {allLabel}
          </MenuItemRadio>
          {options.map((o) => (
            <MenuItemRadio key={o} name={label} value={o}>
              {o}
            </MenuItemRadio>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function Pools({ devices, relayIp }: { devices: Device[]; relayIp: string | null }) {
  const s = useStyles();
  const pools = poolUsage(devices, networksFromDevices(devices));
  return (
    <section aria-label="Address pools">
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Address pools</h2>
        <span className={s.hint}>one /24 per network{relayIp && ` · relay holds ${relayIp}`}</span>
      </div>
      <div className={s.pools}>
        {pools.map((p) => (
          <div key={p.id} className={mergeClasses(s.inset, s.pool)} aria-label={`Pool ${p.id}`} role="group">
            <div className={s.poolHead}>
              <span className={s.poolName}>{p.id}</span>
              <span className={s.mono}>{p.cidr}</span>
            </div>
            <div
              className={s.track}
              role="meter"
              aria-label={`${p.id} usage`}
              aria-valuemin={0}
              aria-valuemax={POOL_HOSTS}
              aria-valuenow={p.used}
            >
              <div className={s.bar} style={{ width: `${Math.max(2, (100 * p.used) / POOL_HOSTS)}%` }} />
            </div>
            <div className={s.poolFoot}>
              <span className={s.mono}>{p.used} used</span>
              <span className={mergeClasses(s.mono, s.faint)}>{p.free} free</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Naming({ devices, relayIp }: { devices: Device[]; relayIp: string | null }) {
  const s = useStyles();
  const named = devices.filter((d) => d.dnsName);
  return (
    <section aria-label="Naming">
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Naming</h2>
        <span className={s.hint}>devices resolve by hostname inside the overlay</span>
      </div>
      <div className={s.naming}>
        <div className={s.inset}>
          <dl className={s.kvs}>
            <dt className={s.k}>Resolver</dt>
            <dd className={mergeClasses(s.v, s.monoStrong)}>{relayIp ? `${relayIp} (relay)` : '–'}</dd>
            <dt className={s.k}>Search suffix</dt>
            <dd className={mergeClasses(s.v, s.monoStrong)}>{DNS_SUFFIX}</dd>
            <dt className={s.k}>Records</dt>
            <dd className={mergeClasses(s.v, s.monoStrong)}>{named.length} · regenerated on peer sync</dd>
          </dl>
          <div className={s.split}>
            <Tooltip content="Fixed: the client only sends *.vpn to the relay" relationship="description">
              <span className={s.splitRow} role="img" aria-label="Split DNS: on">
                <span className={s.toggle} aria-hidden>
                  <span className={s.knob} />
                </span>
                <span>
                  Split DNS<span className={s.splitNote}>only *.{DNS_SUFFIX} goes to the relay</span>
                </span>
              </span>
            </Tooltip>
          </div>
        </div>
        <div>
          <div className={mergeClasses(s.inset, s.records)}>
            <dl className={s.recordGrid} aria-label="DNS records">
              <dt className={s.colLabel}>Name</dt>
              <dd className={mergeClasses(s.colLabel, s.v)} style={{ textAlign: 'start' }}>
                Resolves to
              </dd>
              {named.map((d) => (
                <div key={d.id} style={{ display: 'contents' }}>
                  <dt className={s.mono} style={{ color: vt.fg2 }}>
                    {d.dnsName}
                  </dt>
                  <dd className={mergeClasses(s.record, s.monoStrong)}>
                    {d.overlayIp}
                    {d.addressMode === 'reserved' && <PinMark />}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          {named[0] && (
            <div className={mergeClasses(s.shortcut, s.mono)}>
              <WindowConsole20Regular fontSize={14} />
              <span className={s.faint}>mstsc /v:{named[0].dnsName}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** Re-render once a minute, so "N min ago" and the online window move without asking the API again. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** The device inventory (mockup 03): filters, the table with reserved addresses and tags, pools, naming. */
export function Devices() {
  const s = useStyles();
  const { me, loading, error, canUseVpn, isAdmin } = useMe();
  const { devices, devicesError, reloadDevices, relay, statuses, statusesError, live } = useShell();
  const [onlineOnly, setOnlineOnly] = useState(false);
  const now = useMinute();
  const [query, setQuery] = useState('');
  const [network, setNetwork] = useState<string | null>(null);
  const [tag, setTag] = useState<string | null>(null);

  const list = useMemo(() => devices ?? [], [devices]);
  const networks = useMemo(() => networksFromDevices(list), [list]);
  const online = useMemo(() => (statuses ? onlineIds(list, statuses, live, now) : null), [list, statuses, live, now]);
  const shown = useMemo(
    () => filterDevices(list, { query, network, tag, only: onlineOnly ? online : null }),
    [list, query, network, tag, onlineOnly, online],
  );
  const cidrOf = (id: string | null) => networks.find((n) => n.id === id)?.cidr ?? null;
  const relayIp = relay.relay?.overlayIp ?? null;

  return (
    <>
      <PageHeader title="Devices" actions={me && canUseVpn && <AddDevice />} />

      {loading && <Spinner label="Loading your permissions" />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {me && !canUseVpn && <NoAccess />}
      {me && canUseVpn && (
        <>
          <div className={s.toolbar} role="toolbar" aria-label="Filters">
            <Input
              className={s.search}
              size="small"
              contentBefore={<Search16Regular />}
              placeholder="Search name, address or tag"
              aria-label="Search name, address or tag"
              value={query}
              onChange={(_e, data) => setQuery(data.value)}
            />
            <FilterMenu
              label="Network"
              allLabel="All networks"
              value={network}
              options={networks.map((n) => n.id)}
              onChange={setNetwork}
            />
            <FilterMenu label="Tag" allLabel="All tags" value={tag} options={allTags(list)} onChange={setTag} />
            <Button
              appearance="outline"
              shape="circular"
              size="small"
              className={mergeClasses(s.filter, onlineOnly && s.filterOn)}
              aria-pressed={onlineOnly}
              disabled={!online}
              onClick={() => setOnlineOnly((v) => !v)}
            >
              Online only
            </Button>
            <span className={s.count} aria-live="polite">
              {shown.length === list.length ? list.length : `${shown.length} of ${list.length}`} devices ·{' '}
              {online ? online.size : '–'} online
            </span>
          </div>

          {statusesError && (
            <MessageBar intent="warning">
              <MessageBarBody>Online state unavailable: {statusesError}</MessageBarBody>
            </MessageBar>
          )}
          {devicesError && (
            <MessageBar intent="error">
              <MessageBarBody>{devicesError}</MessageBarBody>
            </MessageBar>
          )}
          {!devices && !devicesError && <Spinner label="Loading devices" />}
          {devices && devices.length === 0 && (
            <p className={s.empty}>
              No devices enrolled yet. <EnrolText />
            </p>
          )}
          {devices && devices.length > 0 && (
            <>
              <DeviceTable
                devices={shown}
                allDevices={list}
                cidrOf={cidrOf}
                me={me}
                isAdmin={isAdmin}
                reload={reloadDevices}
                statuses={statuses}
                live={live}
                now={now}
              />
              {shown.length === 0 && <p className={s.empty}>No device matches the filters.</p>}
              <Pools devices={list} relayIp={relayIp} />
              <Naming devices={list} relayIp={relayIp} />
            </>
          )}
        </>
      )}
    </>
  );
}
